import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Link, Typography } from '@mui/material';
import TextField from '../components/SecretTextField';
import { LoadingSpinner } from '../components/LoadingSpinner';
import WarningDialog from '../components/WarningDialog';
import { forgotPassword, resetPassword } from '../services/AuthService';

import { getErrorMessage } from '../services/ApiError';
import { useCountdown } from '../hooks/useCountdown';

const ForgotPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Steps: 'email' -> 'reset'.
    const [step, setStep] = useState('email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useCountdown();


    const handleSendCode = async (event) => {
        event.preventDefault();
        if (!email.trim()) {
            setError(t('auth.errors.invalidEmail'));
            return;
        }
        try {
            setLoading(true);
            setError('');
            await forgotPassword(email.trim());
            // The API always answers generically to avoid account enumeration.
            setStep('reset');
            setResendCooldown(60);
            setInfo(t('auth.resetCodeSent'));
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.requestFailed')));
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        try {
            setLoading(true);
            setError('');
            await forgotPassword(email.trim());
            setResendCooldown(60);
            setInfo(t('auth.resetCodeSent'));
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.requestFailed')));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async (event) => {
        event.preventDefault();
        if (!/^\d{6}$/.test(code.trim())) {
            setError(t('auth.errors.invalidCode'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t('auth.errors.passwordMismatch'));
            return;
        }
        try {
            setLoading(true);
            setError('');
            await resetPassword(email.trim(), code.trim(), newPassword, confirmPassword);
            navigate('/login', { replace: true });
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.resetFailed')));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="app-page auth-page">
            <LoadingSpinner show={loading} />
            <Card className="app-surface auth-card" sx={{ width: { xs: '90%', sm: '25rem' }, backgroundColor: 'background.paper', color: 'text.primary', padding: 2 }}>
                <CardContent>
                    <Typography className="app-page-title" variant="h5" align="center" sx={{ fontWeight: 'bold', marginBottom: 2, color: 'primary.main' }}>
                        {t('auth.forgotPassword')}
                    </Typography>
                    {info && (
                        <Typography variant="body2" sx={{ marginBottom: 1, color: 'success.main' }}>{info}</Typography>
                    )}
                    {step === 'email' ? (
                        <form onSubmit={handleSendCode}>
                            <TextField
                                label={t('auth.email')}
                                type="email"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <Button type="submit" className="app-btn" variant="contained" fullWidth sx={{ marginTop: 2 }}>
                                {t('auth.sendVerificationCode')}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={handleReset}>
                            <TextField
                                label={t('auth.verificationCode')}
                                type="password"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                            />
                            <TextField
                                label={t('auth.newPassword')}
                                type="password"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                            <TextField
                                label={t('auth.confirmPassword')}
                                type="password"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                            <Button type="submit" className="app-btn" variant="contained" fullWidth sx={{ marginTop: 2 }}>
                                {t('auth.resetPassword')}
                            </Button>
                            <Button
                                variant="text"
                                fullWidth
                                sx={{ marginTop: 1, color: 'primary.main' }}
                                onClick={handleResend}
                                disabled={resendCooldown > 0}
                            >
                                {resendCooldown > 0
                                    ? t('auth.resendIn', { seconds: resendCooldown })
                                    : t('auth.resendCode')}
                            </Button>
                        </form>
                    )}
                    <Box className="auth-links" sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
                        <Link component={RouterLink} to="/login" sx={{ color: 'primary.main' }}>
                            {t('auth.backToLogin')}
                        </Link>
                    </Box>
                </CardContent>
            </Card>
            <WarningDialog
                fullWidth={true}
                open={!!error}
                onClose={() => setError('')}
                title={t('auth.forgotPassword')}
                warningText={error}
            />
        </Box>
    );
};

export default ForgotPassword;

